import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import ScrollSmoother from 'gsap/ScrollSmoother';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

// Only for mouse and trackpad users who have not asked for reduced motion.
// Touch devices keep native scrolling, so the sticky header works as usual there.
// Browsers without matchMedia keep native scrolling as well.
const hasMatchMedia = typeof window.matchMedia === 'function';
const finePointer = hasMatchMedia && window.matchMedia('(pointer: fine)');
const reducedMotion =
  hasMatchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

let headerObserver = null;

// The page content sits in a fixed wrapper and follows the native scroll position
// with an eased transform. The header can no longer be sticky inside that, so it is
// fixed (see _header.scss) and the content reserves room for it via --header-height.
// That is the whole header (top bar included), the same space the sticky header takes
// in the flow, so the layout matches native scrolling as the header shrinks on scroll.
export function initSmoothScroll() {
  killSmoothScroll();

  const wrapper = document.querySelector('.js-smooth-wrapper');
  const content = document.querySelector('.js-smooth-content');
  if (
    !wrapper ||
    !content ||
    !finePointer ||
    !finePointer.matches ||
    reducedMotion.matches
  ) {
    return null;
  }

  document.documentElement.classList.add('has-smooth-scroll');

  const header = document.querySelector('.header');
  const setHeaderHeight = () => {
    document.documentElement.style.setProperty(
      '--header-height',
      `${header.offsetHeight}px`
    );
  };
  if (header) {
    // Set right away: ScrollTriggers measure positions before a ResizeObserver fires.
    setHeaderHeight();
    if ('ResizeObserver' in window) {
      headerObserver = new ResizeObserver(setHeaderHeight);
      headerObserver.observe(header);
    }
  }

  return ScrollSmoother.create({
    wrapper,
    content,
    smooth: 1,
  });
}

function killSmoothScroll() {
  const smoother = ScrollSmoother.get();
  if (smoother) smoother.kill();

  if (headerObserver) {
    headerObserver.disconnect();
    headerObserver = null;
  }

  document.documentElement.classList.remove('has-smooth-scroll');
  document.documentElement.style.removeProperty('--header-height');
}

// Scroll the page so `element` sits `offset` px below the top of the viewport.
// Through the smoother when it runs: its eased transform means jQuery's offset()
// reports the visual position, not the scroll position.
export function scrollToElement(element, offset = 0, duration = 750) {
  if (!element) return;

  const smoother = ScrollSmoother.get();
  if (smoother) {
    smoother.scrollTo(smoother.offset(element, `top ${offset}px`), true);
    return;
  }

  $('html, body').animate(
    { scrollTop: $(element).offset().top - offset + 'px' },
    duration,
    'swing'
  );
}

export function scrollToTop(duration = 600) {
  const smoother = ScrollSmoother.get();
  if (smoother) {
    smoother.scrollTo(0, true);
    return;
  }

  $('html, body').animate({ scrollTop: 0 }, duration);
}

// Pick the smoother up or drop it when the input device or motion preference changes.
// Older Safari only has the deprecated addListener().
const onPreferenceChange = () => {
  if (!document.querySelector('.js-smooth-wrapper')) return;
  initSmoothScroll();
  ScrollTrigger.refresh();
};
if (hasMatchMedia) {
  [finePointer, reducedMotion].forEach((query) => {
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', onPreferenceChange);
    } else if (typeof query.addListener === 'function') {
      query.addListener(onPreferenceChange);
    }
  });
}
